import { lazy, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { UserProvider } from '@/contexts/UserContext';
import { EventTutorialProvider } from '@/contexts/EventTutorialContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import MainLayout from '@/layouts/MainLayout';
import { CHANGELOG_MONTH_ROUTE, PUBLIC_EVENT_ROUTE } from '@/constants/routes';
import { POLICY_DOC } from '@/constants/policyRevisions';
import OnboardingGuard from '@/components/OnboardingGuard';
import ScrollToTop from '@/components/ScrollToTop';
import PageViewBeacon from '@/components/PageViewBeacon';
import AdminRoute from '@/components/AdminRoute';
import LoadingTips from '@/components/common/LoadingTips';
import RouteErrorBoundary from '@/components/common/AppErrorBoundary';
import NavigationLoading from '@/components/common/NavigationLoading';

// 入口の Home はランディングで最も踏まれるため eager のまま残す（初回表示のちらつき回避）。
import Home from '@/pages/Home';

// ── ルート単位のコード分割（#202）──
// 入口（Home / MainLayout / 認証ガード等）以外のページは、そのルートに遷移するまで
// チャンクをダウンロードしないよう React.lazy + 動的 import にする。これにより初回 JS
// （index-*.js）から全ページ分のコードを外し、解凍・パース・実行の CPU コスト（TTI）を下げる。
//
// 注意（#205）: react-router v7 + React 19 では <Link> ナビゲーションが startTransition で
// ラップされ、遷移中は「古い画面を保持」して Suspense フォールバックを出さない。以前ここで
// チャンク解決を人為的に遅らせて最低表示時間を作っていたが、それは遷移先に切り替わるまで
// 古い画面を余計に居座らせるだけだったため撤去した。ヒントの最低表示時間は、遷移先ページの
// データ取得ローダー（useHeldLoading + LoadingTips）が担う。直リンク/初回ロードなど古い画面が
// 無い場合のみ、この <Suspense fallback={<LoadingTips />}> が表示される。
const Features = lazy(() => import('@/pages/Features'));
const FeatureArticle = lazy(() => import('@/pages/FeatureArticle'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Auth = lazy(() => import('@/pages/Auth'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Tokushoho = lazy(() => import('@/pages/Tokushoho'));
const DataSources = lazy(() => import('@/pages/DataSources'));
const Contact = lazy(() => import('@/pages/Contact'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CreateEvent = lazy(() => import('@/pages/CreateEvent'));
const EventDetail = lazy(() => import('@/pages/EventDetail'));
const EditEvent = lazy(() => import('@/pages/EditEvent'));
const AcceptCollaboration = lazy(() => import('@/pages/AcceptCollaboration'));
const PublicEventView = lazy(() => import('@/pages/PublicEventView'));
const Events = lazy(() => import('@/pages/Events'));
const Profile = lazy(() => import('@/pages/Profile'));
const Logs = lazy(() => import('@/pages/Logs'));
const EventsTimeline = lazy(() => import('@/pages/EventsTimeline'));
const CompleteProfile = lazy(() => import('@/pages/CompleteProfile'));
const Consent = lazy(() => import('@/pages/Consent'));
const PolicyArchive = lazy(() => import('@/pages/PolicyArchive'));
const AccountNotFound = lazy(() => import('@/pages/AccountNotFound'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Help = lazy(() => import('@/pages/Help'));
const AccountDeleted = lazy(() => import('@/pages/AccountDeleted'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Unsubscribe = lazy(() => import('@/pages/Unsubscribe'));
const LineCallback = lazy(() => import('@/pages/LineCallback'));
const Tools = lazy(() => import('@/pages/Tools'));
const MidpointStation = lazy(() => import('@/pages/tools/MidpointStation'));
const RoutePlanner = lazy(() => import('@/pages/tools/RoutePlanner'));
const SpotShare = lazy(() => import('@/pages/tools/SpotShare'));
const Warikan = lazy(() => import('@/pages/tools/Warikan'));
const RandomPicker = lazy(() => import('@/pages/tools/RandomPicker'));
const PackingList = lazy(() => import('@/pages/tools/PackingList'));
const Timetable = lazy(() => import('@/pages/tools/Timetable'));
const ShoppingList = lazy(() => import('@/pages/tools/ShoppingList'));
const ReservationList = lazy(() => import('@/pages/tools/ReservationList'));
const EventIdeas = lazy(() => import('@/pages/tools/EventIdeas'));
const KokeSlot = lazy(() => import('@/pages/tools/KokeSlot'));
const KokeCatcher = lazy(() => import('@/pages/tools/KokeCatcher'));
const KokeTsumi = lazy(() => import('@/pages/tools/KokeTsumi'));
const KokeMemory = lazy(() => import('@/pages/tools/KokeMemory'));
const KokeCrush = lazy(() => import('@/pages/tools/KokeCrush'));
const Changelog = lazy(() => import('@/pages/Changelog'));
const ChangelogMonth = lazy(() => import('@/pages/ChangelogMonth'));
const BlogIndex = lazy(() => import('@/pages/BlogIndex'));
const BlogArticle = lazy(() => import('@/pages/BlogArticle'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));

/**
 * 旧 URL (/login, /register) を /auth に統一するためのリダイレクト。
 * クエリパラメータ (returnTo, redirect 等) は維持し、register モードは
 * mode=register を付与する。内部リンクはすべて /auth に書き換え済みのため
 * このリダイレクトは外部からのアクセス時のみ発動する。
 */
function LegacyAuthRedirect({ mode }: { mode: 'login' | 'register' }) {
  const [searchParams] = useSearchParams();
  const target = new URLSearchParams(searchParams);
  if (mode === 'register' && !target.has('mode')) {
    target.set('mode', 'register');
  }
  const qs = target.toString();
  return <Navigate to={qs ? `/auth?${qs}` : '/auth'} replace />;
}

// React Query設定
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5分間はデータを新鮮として扱う
      refetchOnWindowFocus: false, // ウィンドウフォーカス時の自動再取得を無効化
      retry: 1, // エラー時のリトライ回数
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <EventTutorialProvider>
          <BrowserRouter>
            <ScrollToTop />
            <PageViewBeacon />
            <NavigationLoading />
            <Toaster
              position="top-center"
              toastOptions={{
                // 既定より少し長めに表示する（読み切る前に消えるのを防ぐ）。
                // 成功は react-hot-toast 既定が 2 秒と短いので明示的に延長、
                // エラーは読む時間が要るので最長にする。
                duration: 5000,
                success: { duration: 5000 },
                error: { duration: 7000 },
              }}
            />
            <OnboardingGuard />
            <RouteErrorBoundary>
            <Suspense fallback={<LoadingTips className="bg-text-50" />}>
            <Routes>
              {/* 公開ルート */}
              <Route path="/" element={<MainLayout />}>
                <Route index element={<Home />} />
                <Route path="features" element={<Features />} />
                <Route path="features/:slug" element={<FeatureArticle />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="auth" element={<Auth />} />
                {/* 旧 URL の互換用リダイレクト (検索結果・古いブックマーク・メール経由) */}
                <Route path="login" element={<LegacyAuthRedirect mode="login" />} />
                <Route path="register" element={<LegacyAuthRedirect mode="register" />} />
                <Route path="verify-email" element={<VerifyEmail />} />
                <Route path="terms" element={<Terms />} />
                <Route path="privacy" element={<Privacy />} />
                {/* 過去の版（改定履歴・改定の告知からリンク） */}
                <Route path="terms/versions/:version" element={<PolicyArchive doc={POLICY_DOC.TERMS} />} />
                <Route path="privacy/versions/:version" element={<PolicyArchive doc={POLICY_DOC.PRIVACY} />} />
                <Route path="tokushoho" element={<Tokushoho />} />
                <Route path="data-sources" element={<DataSources />} />
                <Route path="complete-profile" element={<CompleteProfile />} />
                <Route path="consent" element={<Consent />} />
                <Route path="account-not-found" element={<AccountNotFound />} />
                <Route path="contact" element={<Contact />} />
                {/* 公開イベントページ（ゲストアクセス可能） */}
                <Route path={PUBLIC_EVENT_ROUTE} element={<PublicEventView />} />
                <Route path="tools" element={<Tools />} />
                <Route path="tools/midpoint-station" element={<MidpointStation />} />
                <Route path="tools/route-planner" element={<RoutePlanner />} />
                <Route path="tools/spot-share" element={<SpotShare />} />
                <Route path="tools/warikan" element={<Warikan />} />
                <Route path="tools/random-picker" element={<RandomPicker />} />
                <Route path="tools/packing-list" element={<PackingList />} />
                <Route path="tools/timetable" element={<Timetable />} />
                <Route path="tools/shopping-list" element={<ShoppingList />} />
                <Route path="tools/reservation-list" element={<ReservationList />} />
                <Route path="tools/event-ideas" element={<EventIdeas />} />
                <Route path="tools/koke-slot" element={<KokeSlot />} />
                <Route path="tools/koke-catcher" element={<KokeCatcher />} />
                <Route path="tools/koke-tsumi" element={<KokeTsumi />} />
                <Route path="tools/koke-memory" element={<KokeMemory />} />
                <Route path="tools/koke-crush" element={<KokeCrush />} />
                <Route path="changelog" element={<Changelog />} />
                <Route path={CHANGELOG_MONTH_ROUTE} element={<ChangelogMonth />} />
                <Route path="blog" element={<BlogIndex />} />
                <Route path="blog/:slug" element={<BlogArticle />} />
                <Route path="help" element={<Help />} />
                <Route path="account-deleted" element={<AccountDeleted />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="unsubscribe" element={<Unsubscribe />} />
                <Route path="settings/line/callback" element={<LineCallback />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* 保護されたルート（認証が必要） */}
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<MainLayout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="events/create" element={<CreateEvent />} />
                  <Route path="events" element={<Events />} />
                  <Route path="events/timeline" element={<EventsTimeline />} />
                  <Route path="events/:eventId" element={<EventDetail />} />
                  <Route path="events/:eventId/edit" element={<EditEvent />} />
                  <Route
                    path="events/:eventId/accept-collab"
                    element={<AcceptCollaboration />}
                  />
                  <Route path="profile" element={<Profile />} />
                  <Route path="logs" element={<Logs />} />
                </Route>
              </Route>

              {/* 管理者専用ルート（Cognito 'admin' グループ必須） */}
              <Route element={<AdminRoute />}>
                <Route path="/" element={<MainLayout />}>
                  <Route path="admin" element={<AdminDashboard />} />
                </Route>
              </Route>
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
          </BrowserRouter>
          </EventTutorialProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
